import { useEffect, useState } from "react";
import { t } from "./lib/sprache";
import { istInternHost, istInternPfad } from "./lib/bereich";

/**
 * Installierbare App für beide Portale (Befund 7 der Creator-Prüfung). Je Portal ein Manifest (Name „CreatorOffice“,
 * Symbole aus public/, Start im eigenen Portal); der Service Worker (public/sw.js) dient nur Installation und
 * Offline-Hinweis, mit strikter Aktualisierung. Im Dev-Server und im Demo-Modus wird er nie registriert, ein früher
 * registrierter Worker wird dort entfernt.
 */

export interface ServiceWorkerEnv {
  dev: boolean;
  demo: boolean;
  supported: boolean;
}

export function shouldRegisterServiceWorker(env: ServiceWorkerEnv): boolean {
  return env.supported && !env.dev && !env.demo;
}

/**
 * Manifest je Portal: Creator-Portal und Einladung starten im Creator-Portal, der interne Bereich (/intern, Betreiber-Konsole)
 * ist eine eigene App („CreatorOffice Intern“, Inhaber 24.10. und 03.11.2026: im Dock des Mac mit eigenem Symbol und eigener
 * Anmeldung), alles andere im Agentur-Bereich.
 */
export function manifestFor(pathname: string): string {
  // Interner Bereich (Inhaber 03.11.2026): eigene App „CreatorOffice Intern“ mit eigener Anmeldung; alte /betrieb-Adressen ebenso
  if (istInternPfad(pathname) || (typeof location !== "undefined" && istInternHost(location.hostname))) return "/manifest-konsole.webmanifest";
  return pathname === "/creator" || pathname.startsWith("/creator/") || pathname.startsWith("/einladung") ? "/manifest-creator.webmanifest" : "/manifest-agentur.webmanifest";
}

/** Symbol und Name für „Zum Dock hinzufügen“ in Safari (liest apple-touch-icon und apple-mobile-web-app-title, nicht das Manifest). */
export function appleKopfFor(pathname: string): { icon: string; title: string } {
  return manifestFor(pathname) === "/manifest-konsole.webmanifest"
    ? { icon: "/konsole-touch-icon.png", title: "CreatorOffice Intern" }
    : { icon: "/apple-touch-icon.png", title: "CreatorOffice" };
}

/** Adresse des Workers mit Build-Version: ein neuer Build ist ein neuer Worker (versionierter Speicher). */
export function serviceWorkerUrl(version: string): string {
  return `/sw.js?v=${encodeURIComponent(version || "0")}`;
}

/** Version des Builds aus dem Namen der gebauten Datei (assets/index-<prüfsumme>.js). */
export function buildVersion(moduleUrl: string): string {
  const file = moduleUrl.split("?")[0]!.split("/").pop() ?? "";
  return file.replace(/\.[a-z]+$/i, "") || "0";
}

export function setManifest(pathname: string): void {
  if (typeof document === "undefined") return;
  let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "manifest";
    document.head.appendChild(link);
  }
  const href = manifestFor(pathname);
  if (link.getAttribute("href") !== href) link.setAttribute("href", href);
  const kopf = appleKopfFor(pathname);
  const icon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  if (icon && icon.getAttribute("href") !== kopf.icon) icon.setAttribute("href", kopf.icon);
  const title = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
  if (title && title.content !== kopf.title) title.content = kopf.title;
}

/** Beim Start: Manifest wählen, Worker registrieren oder (Dev, Demo) entfernen, Installationsangebot merken. */
export function setupPwa(opts: { dev: boolean; demo: boolean }): void {
  if (typeof window === "undefined") return;
  setManifest(location.pathname);
  listenForInstallPrompt();
  const supported = "serviceWorker" in navigator;
  if (!supported) return;
  if (!shouldRegisterServiceWorker({ ...opts, supported })) {
    // Ein Worker aus einer früheren Sitzung darf im Dev-Server und in der Demo nichts ausliefern
    void navigator.serviceWorker.getRegistrations().then((list) => list.forEach((r) => {
      const url = r.active?.scriptURL ?? r.installing?.scriptURL ?? r.waiting?.scriptURL ?? "";
      if (/\/sw\.js(\?|$)/.test(url)) void r.unregister();
    })).catch(() => undefined);
    return;
  }
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register(serviceWorkerUrl(buildVersion(import.meta.url)), { scope: "/", updateViaCache: "none" })
      .then((reg) => {
        // Lange offene Tabs fragen stündlich nach einem neuen Build, dazu beim Zurückkehren in den Tab
        setInterval(() => void reg.update().catch(() => undefined), 60 * 60_000);
        // Prüf-Chat 30.10.2026 (F3): sw.js ist je Build gleich, die Version steht nur in der Adresse; reg.update() findet deshalb nie
        // einen neuen Worker. Stattdessen beim Zurückkehren in den Tab und alle 10 Minuten die Startseite ohne Speicher holen und den
        // Namen des gebauten Bündels mit dem laufenden vergleichen; nur bei einem anderen Build erscheint der Hinweis.
        const eigene = buildVersion(import.meta.url);
        const pruefen = () => void neuerBuild(eigene).then((ja) => { if (ja) neueVersionHinweis(); }).catch(() => undefined);
        document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") pruefen(); });
        setInterval(pruefen, 10 * 60_000);
      })
      .catch(() => undefined);
  });
}

/** Liefert der Server ein anderes Bündel als das laufende? (Startseite ohne Speicher, der Worker fasst fetch() nicht an) */
export async function neuerBuild(eigene: string): Promise<boolean> {
  if (!eigene || eigene === "0") return false;
  const r = await fetch(`/?build=${Date.now()}`, { cache: "no-store", credentials: "same-origin" });
  if (!r.ok) return false;
  const m = /\/assets\/(index-[A-Za-z0-9_-]+)\.js/.exec(await r.text());
  return !!m && m[1] !== eigene;
}

/** Hinweis unten in der Mitte: neue Version installiert, ein Klick lädt neu (einmal je Seite) */
function neueVersionHinweis() {
  if (typeof document === "undefined" || document.getElementById("tk-neue-version")) return;
  const box = document.createElement("div");
  box.id = "tk-neue-version";
  box.setAttribute("role", "status");
  const text = document.createElement("span");
  text.textContent = t("Neue Version da", "New version available");
  const knopf = document.createElement("button");
  knopf.type = "button";
  knopf.textContent = t("Neu laden", "Reload");
  knopf.addEventListener("click", () => window.location.reload());
  box.append(text, knopf);
  document.body.append(box);
}

// ── Installationsangebot ─────────────────────────────────────────────────────

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
let listening = false;

function listenForInstallPrompt() {
  if (listening) return;
  listening = true;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    listeners.forEach((l) => l());
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    listeners.forEach((l) => l());
  });
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return !!window.matchMedia?.("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;
}

export function isAppleMobile(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferred) return "unavailable";
  const e = deferred;
  deferred = null;
  await e.prompt();
  const choice = await e.userChoice;
  listeners.forEach((l) => l());
  return choice.outcome;
}

/** Stand für die Seite „Konto“: installierbar (Browser bietet es an), schon installiert, Apple-Gerät (Weg über „Teilen“). */
export function useInstallState(): { canPrompt: boolean; standalone: boolean; apple: boolean } {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);
  return { canPrompt: !!deferred, standalone: isStandalone(), apple: isAppleMobile() };
}
